import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import { Divider, Grid, IconButton, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { decode } from 'he';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import useSubscriberVaultAlbums, { OnlyFansVaultAlbums } from '../../hooks/useSubscriberVaultAlbums';
import StyledLocalMenu from './StyledLocalMenu';

const LocalMenu = ({
    sortAlbums,
    setSortAlbums,
    isLoading,
    isCreatingAlbum,
    refetchAlbums,
    showCreateAlbumButton,
    setIsCreateAlbumDialogOpen,
    disabled,
}: {
    sortAlbums: boolean;
    setSortAlbums: (sortLists: boolean) => void;
    isLoading: boolean;
    isCreatingAlbum: boolean;
    refetchAlbums: () => void;
    showCreateAlbumButton?: boolean;
    setIsCreateAlbumDialogOpen: (isOpen: boolean) => void;
    disabled?: boolean;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const sortMenuOpen = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton disabled={disabled} onClick={e => handleMenuClick(e)}>
                <MoreHorizIcon />
            </IconButton>
            <StyledLocalMenu
                id="trial-menu"
                MenuListProps={{
                    'aria-labelledby': 'trial-button',
                }}
                anchorEl={anchorEl}
                open={sortMenuOpen}
                onClose={handleMenuClose}
            >
                <MenuItem
                    disabled={isLoading || isCreatingAlbum}
                    onClick={() => {
                        setSortAlbums(!sortAlbums);
                        handleMenuClose();
                    }}
                    disableRipple
                >
                    <SortByAlphaIcon color={sortAlbums ? 'primary' : 'secondary'} />
                    {sortAlbums ? 'Custom sort like on OnlyFans' : 'Sort lists alphabetically'}
                </MenuItem>
                <Divider />
                <MenuItem
                    disabled={isLoading || isCreatingAlbum}
                    onClick={() => {
                        refetchAlbums();
                        handleMenuClose();
                    }}
                    disableRipple
                >
                    <RefreshIcon color="secondary" />
                    Reload Albums from OnlyFans
                </MenuItem>
                {showCreateAlbumButton && [
                    <Divider key={'divider'} />,
                    <MenuItem
                        key={'create-list'}
                        disabled={isCreatingAlbum || disabled}
                        onClick={() => {
                            setIsCreateAlbumDialogOpen(true);
                            handleMenuClose();
                        }}
                        disableRipple
                    >
                        <AddIcon color="success" />
                        Create new OnlyFans Album...
                    </MenuItem>,
                ]}
            </StyledLocalMenu>
        </>
    );
};

const OnlyFansAlbumSelector = ({
    vaultAlbum,
    handleAlbumChange,
    disabled = false,
    size = 'medium',
}: {
    vaultAlbum: number;
    handleAlbumChange: (e: SelectChangeEvent<any>) => void;
    disabled?: boolean;
    size?: 'small' | 'medium';
}) => {
    const [sortAlbums, setSortAlbums] = useState<boolean>(true);
    const [sortedAlbums, setSortedAlbums] = useState<OnlyFansVaultAlbums.List[] | undefined>([]);

    const {
        data: vaultAlbums,
        fetchNextPage: fetchNextPageAlbums,
        hasNextPage: vaultAlbumHasNextPage,
        isFetchingNextPage: vaultAlbumIsFetchingNextPage,
        refetch: refetchAlbums,
        isLoading: vaultAlbumsLoading,
        isRefetching: vaultAlbumIsRefetching,
        clearCacheForVaultAlbumRefresh,
    } = useSubscriberVaultAlbums(true);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (vaultAlbumHasNextPage && !vaultAlbumIsFetchingNextPage) {
            fetchNextPageAlbums();
        }
    }, [vaultAlbumHasNextPage, vaultAlbumIsFetchingNextPage, fetchNextPageAlbums]);

    const processedAlbums = useMemo(() => {
        if (!vaultAlbums?.pages) return [];

        const allAlbums = vaultAlbums.pages.flatMap(page => page.list);

        if (!sortAlbums) return allAlbums;

        return [...allAlbums].sort((a, b) => a.name.localeCompare(b.name));
    }, [vaultAlbums, sortAlbums]);

    useEffect(() => {
        setSortedAlbums(processedAlbums);
    }, [processedAlbums]);

    return (
        <Grid container spacing={0} flexGrow={0} alignItems="center" justifyContent="center">
            <Grid item xs>
                <Select
                    labelId="vaultAlbum"
                    defaultValue={0}
                    value={vaultAlbum}
                    disabled={disabled || vaultAlbumsLoading || vaultAlbumIsRefetching}
                    onChange={handleAlbumChange}
                    fullWidth
                    size={size}
                    error={vaultAlbum === -1}
                >
                    {(vaultAlbumsLoading || vaultAlbumIsFetchingNextPage) && (
                        <MenuItem value={0} disabled>
                            {`Loading Albums... ${
                                sortedAlbums && sortedAlbums.length > 0 ? `(${sortedAlbums.length.toLocaleString()})` : ''
                            }`}
                        </MenuItem>
                    )}
                    {!vaultAlbumsLoading && !vaultAlbumIsFetchingNextPage && (
                        <MenuItem value={0}>
                            <strong>All</strong>
                        </MenuItem>
                    )}

                    {!vaultAlbumsLoading &&
                        sortedAlbums &&
                        sortedAlbums.map(album => (
                            <MenuItem value={album.id} key={album.id}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{decode(album.name)}</div>
                            </MenuItem>
                        ))}
                </Select>
            </Grid>
            <Grid item xs={'auto'} sx={{ pl: 0.5 }}>
                <LocalMenu
                    sortAlbums={sortAlbums}
                    setSortAlbums={setSortAlbums}
                    isLoading={vaultAlbumsLoading || vaultAlbumIsRefetching}
                    isCreatingAlbum={false}
                    refetchAlbums={async () => {
                        await clearCacheForVaultAlbumRefresh();
                        // Drop the cached pages before refetching. A refetch on an infinite
                        // query keeps the old data until every page has reloaded, so
                        // sortedAlbums would otherwise show the stale albums with no loading
                        // state. Removing the query puts the hook into isLoading and the
                        // next-page effect loads the rest progressively.
                        queryClient.removeQueries('vaultAlbums');
                        setSortedAlbums([]);
                        await refetchAlbums();
                    }}
                    showCreateAlbumButton={false}
                    setIsCreateAlbumDialogOpen={() => {}}
                    disabled={vaultAlbum === -1}
                />
            </Grid>
        </Grid>
    );
};

export default OnlyFansAlbumSelector;
